import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import Swal from 'sweetalert2';
import { AdminEditPermissionsService } from 'src/app/shared/services/admin-edit-permissions.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { ComisionesBancariasService } from '../../services/comisiones-bancarias.service';
import { BanksService } from '../../services/banks.service';
import {
  ComisionBancaria,
  TipoOperacion,
  TipoValor,
  TipoCuenta,
  CreateComisionDto,
  UpdateComisionDto,
  PaginationMeta
} from '../../interfaces/comision-bancaria';
import { Banco } from '../../interfaces/banco';

@Component({
  selector: 'app-comisiones-bancarias',
  templateUrl: './comisiones-bancarias.component.html',
  styleUrls: ['./comisiones-bancarias.component.sass']
})
export class ComisionesBancariasComponent implements OnInit {
  displayedColumns: string[] = [
    'banco',
    'tipoCuenta',
    'tipoOperacion',
    'tipoValor',
    'porcentaje',
    'valorFijo',
    'activo',
    'responsableEmail',
    'createdAt',
    'acciones'
  ];

  dataSource: MatTableDataSource<ComisionBancaria> = new MatTableDataSource();
  bancos: Banco[] = [];
  /**
   * Candado de edición: en producción solo escribe el operador autorizado.
   * No deshabilito `comisionForm` porque el form solo se abre desde
   * `openCreateForm` / `openEditForm`, que ya están bloqueados; tocarlo
   * chocaría con los validadores dinámicos de `setupFormValidators`. El
   * `filterForm` queda vivo: es de búsqueda, no de escritura.
   */
  puedeEditar = false;

  comisionForm: FormGroup;
  filterForm: FormGroup;
  isEditing = false;
  editingId: number | null = null;
  showForm = false;
  showFilters = false;
  expandedCardId: number | null = null;

  // Paginación
  paginationMeta: PaginationMeta = {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    from: 0,
    to: 0
  };
  pageSize = 20;
  pageSizeOptions = [10, 20, 50, 100];

  tiposOperacion = Object.values(TipoOperacion);
  tiposValor = Object.values(TipoValor);
  tiposCuenta = Object.values(TipoCuenta);

  tipoCuentaLabels: { [key: string]: string } = {
    'personal': 'Personal',
    'juridica': 'Jurídica',
  };

  tipoOperacionLabels: { [key: string]: string } = {
    'transferencia_enviada_otros_bancos_persona': 'Transferencia Enviada Otros Bancos (Persona)',
    'transferencia_enviada_otros_bancos_empresa': 'Transferencia Enviada Otros Bancos (Empresa)',
    'transferencia_enviada_mismo_banco_persona': 'Transferencia Enviada Mismo Banco (Persona)',
    'transferencia_enviada_mismo_banco_empresa': 'Transferencia Enviada Mismo Banco (Empresa)',
    'transferencia_recibida_otro_banco_persona': 'Transferencia Recibida Otro Banco (Persona)',
    'transferencia_recibida_otro_banco_empresa': 'Transferencia Recibida Otro Banco (Empresa)',
    'transferencia_recibida_mismo_banco_persona': 'Transferencia Recibida Mismo Banco (Persona)',
    'transferencia_recibida_mismo_banco_empresa': 'Transferencia Recibida Mismo Banco (Empresa)',
    'pago_movil_recibido': 'Pago Móvil Recibido',
    'pago_movil_enviado': 'Pago Móvil Enviado',
    'transferencia_mismo_titular': 'Transferencia Mismo Titular'
  };

  constructor(
    private fb: FormBuilder,
    private comisionesService: ComisionesBancariasService,
    private banksService: BanksService,
    private spinner: SpinnerService,
    public dialog: MatDialog,
    private permisos: AdminEditPermissionsService,
  ) {
    this.puedeEditar = this.permisos.valorInicial;
    this.permisos.puedeEditar().then((puede) => (this.puedeEditar = puede));

    this.comisionForm = this.fb.group({
      bancoId: [null, Validators.required],
      tipoCuenta: [TipoCuenta.PERSONAL, Validators.required],
      tipoOperacion: [null, Validators.required],
      tipoValor: [TipoValor.PORCENTAJE, Validators.required],
      porcentaje: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      valorFijo: [null]
    });

    this.filterForm = this.fb.group({
      bancoId: [null],
      tipoCuenta: [null],
      tipoOperacion: [null],
      activo: [true]
    });
  }

  async ngOnInit() {
    await this.loadBancos();
    await this.loadComisiones();
    this.setupFormValidators();
  }

  setupFormValidators() {
    this.comisionForm.get('tipoValor')?.valueChanges.subscribe((tipoValor) => {
      const porcentajeControl = this.comisionForm.get('porcentaje');
      const valorFijoControl = this.comisionForm.get('valorFijo');

      porcentajeControl?.clearValidators();
      valorFijoControl?.clearValidators();

      if (tipoValor === TipoValor.PORCENTAJE) {
        porcentajeControl?.setValidators([
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          this.maxDecimalsValidator(2)
        ]);
        if (!porcentajeControl?.value) {
          porcentajeControl?.setValue(0);
        }
        valorFijoControl?.setValue(null);
      } else if (tipoValor === TipoValor.FIJO) {
        valorFijoControl?.setValidators([
          Validators.required,
          Validators.min(0),
          this.maxDecimalsValidator(2)
        ]);
        if (!valorFijoControl?.value) {
          valorFijoControl?.setValue(0);
        }
        porcentajeControl?.setValue(null);
      }

      porcentajeControl?.updateValueAndValidity();
      valorFijoControl?.updateValueAndValidity();
    });
  }

  async loadBancos() {
    const ref = this.spinner.start();
    try {
      this.bancos = await this.banksService.getBanksByCountry('VE');
      this.spinner.stop(ref);
    } catch (error) {
      this.spinner.stop(ref);
      Swal.fire('Error', 'No se pudieron cargar los bancos', 'error');
    }
  }

  async loadComisiones() {
    const ref = this.spinner.start();
    try {
      const filters = this.filterForm.value;
      const pagination = {
        page: this.paginationMeta.page,
        limit: this.pageSize,
        sortBy: 'createdAt',
        sortOrder: 'DESC' as 'DESC'
      };

      const response = await this.comisionesService.findAll(filters, pagination);
      this.dataSource.data = response.data;
      this.paginationMeta = response.meta;
      this.spinner.stop(ref);
    } catch (error) {
      this.spinner.stop(ref);
      Swal.fire('Error', 'No se pudieron cargar las comisiones', 'error');
    }
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.paginationMeta.page = event.pageIndex + 1;
    this.loadComisiones();
  }

  applyFilter() {
    this.loadComisiones();
  }

  clearFilter() {
    this.filterForm.reset({ activo: true });
    this.loadComisiones();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  openCreateForm() {
    if (this.permisos.bloqueado(this.puedeEditar, 'Tu usuario no tiene permisos para modificar las comisiones bancarias.')) return;

    this.isEditing = false;
    this.editingId = null;
    this.comisionForm.reset({
      tipoCuenta: TipoCuenta.PERSONAL,
      tipoValor: TipoValor.PORCENTAJE,
      porcentaje: 0,
      valorFijo: null
    });
    this.showForm = true;
  }

  openEditForm(comision: ComisionBancaria) {
    if (this.permisos.bloqueado(this.puedeEditar, 'Tu usuario no tiene permisos para modificar las comisiones bancarias.')) return;

    this.isEditing = true;
    this.editingId = comision.comisionId;

    // Establecer el tipo de valor primero para que se configuren los validadores correctos
    this.comisionForm.get('tipoValor')?.setValue(comision.tipoValor);

    // Luego establecer los demás valores
    this.comisionForm.patchValue({
      bancoId: comision.bancoId,
      tipoCuenta: comision.tipoCuenta,
      tipoOperacion: comision.tipoOperacion,
      porcentaje: comision.porcentaje,
      valorFijo: comision.valorFijo
    });

    this.comisionForm.get('bancoId')?.disable();
    this.comisionForm.get('tipoCuenta')?.disable();
    this.comisionForm.get('tipoOperacion')?.disable();
    this.showForm = true;
  }

  cancelForm() {
    this.showForm = false;
    this.isEditing = false;
    this.editingId = null;
    this.comisionForm.reset({
      tipoCuenta: TipoCuenta.PERSONAL,
      tipoValor: TipoValor.PORCENTAJE,
      porcentaje: 0,
      valorFijo: null
    });
    this.comisionForm.get('bancoId')?.enable();
    this.comisionForm.get('tipoCuenta')?.enable();
    this.comisionForm.get('tipoOperacion')?.enable();
  }

  async saveComision() {
    if (this.permisos.bloqueado(this.puedeEditar, 'Tu usuario no tiene permisos para modificar las comisiones bancarias.')) return;

    if (this.comisionForm.invalid) {
      Swal.fire('Formulario inválido', 'Por favor complete todos los campos requeridos', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: this.isEditing ? '¿Actualizar comisión?' : '¿Crear comisión?',
      text: this.isEditing ? 'Se creará una nueva versión y se desactivará la anterior' : 'Se creará una nueva comisión bancaria',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    const ref = this.spinner.start();
    try {
      const formValue = this.comisionForm.getRawValue();

      if (this.isEditing && this.editingId) {
        const updateDto: UpdateComisionDto = {
          tipoCuenta: formValue.tipoCuenta,
          tipoValor: formValue.tipoValor,
          porcentaje: formValue.tipoValor === TipoValor.PORCENTAJE ? formValue.porcentaje : null,
          valorFijo: formValue.tipoValor === TipoValor.FIJO ? formValue.valorFijo : null
        };
        await this.comisionesService.update(this.editingId, updateDto);
      } else {
        const createDto: CreateComisionDto = {
          bancoId: formValue.bancoId,
          codigoPais: 'VE',
          tipoCuenta: formValue.tipoCuenta,
          tipoOperacion: formValue.tipoOperacion,
          tipoValor: formValue.tipoValor,
          porcentaje: formValue.tipoValor === TipoValor.PORCENTAJE ? formValue.porcentaje : null,
          valorFijo: formValue.tipoValor === TipoValor.FIJO ? formValue.valorFijo : null
        };
        await this.comisionesService.create(createDto);
      }

      this.spinner.stop(ref);
      Swal.fire('Éxito', this.isEditing ? 'Comisión actualizada correctamente' : 'Comisión creada correctamente', 'success');
      this.cancelForm();
      await this.loadComisiones();
    } catch (error: any) {
      this.spinner.stop(ref);
      Swal.fire('Error', error?.error?.message || 'No se pudo guardar la comisión', 'error');
    }
  }

  async toggleActivo(comision: ComisionBancaria) {
    if (this.permisos.bloqueado(this.puedeEditar, 'Tu usuario no tiene permisos para modificar las comisiones bancarias.')) return;

    const nuevoEstado = comision.activo === 1 ? 0 : 1;
    const textoAccion = nuevoEstado === 1 ? 'activar' : 'desactivar';

    const result = await Swal.fire({
      title: `¿${textoAccion.charAt(0).toUpperCase() + textoAccion.slice(1)} comisión?`,
      text: `Se va a ${textoAccion} esta comisión`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevoEstado === 1 ? '#1C593D' : '#dc2626',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    const ref = this.spinner.start();
    try {
      await this.comisionesService.toggle(comision.comisionId, nuevoEstado === 1);
      this.spinner.stop(ref);
      Swal.fire('Éxito', `Comisión ${textoAccion}da`, 'success');
      await this.loadComisiones();
    } catch (error: any) {
      this.spinner.stop(ref);
      Swal.fire('Error', error?.error?.message || 'No se pudo cambiar el estado', 'error');
    }
  }

  async verHistorial(comision: ComisionBancaria) {
    const ref = this.spinner.start();
    try {
      const historial = await this.comisionesService.getHistorial(comision.bancoId, comision.tipoOperacion);
      this.spinner.stop(ref);

      const banco = this.bancos.find(b => b.bancoID === comision.bancoId);
      const bancoNombre = banco?.nombre || comision.bancoId.toString();
      const operacionLabel = this.tipoOperacionLabels[comision.tipoOperacion] || comision.tipoOperacion;
      const isMobile = window.innerWidth < 768;

      let htmlContent = `<div style="text-align: left;">`;

      // Header info
      htmlContent += `<div style="background: linear-gradient(135deg, #f0fdf4, #ecfdf5); padding: 12px 16px; border-radius: 10px; margin-bottom: 16px; border: 1px solid #bbf7d0;">`;
      htmlContent += `<p style="margin: 0 0 4px; font-size: 0.85rem; color: #1e5a3f; font-weight: 700;">Banco: <span style="font-weight: 400; color: #374151;">${bancoNombre}</span></p>`;
      htmlContent += `<p style="margin: 0; font-size: 0.85rem; color: #1e5a3f; font-weight: 700;">Operación: <span style="font-weight: 400; color: #374151;">${operacionLabel}</span></p>`;
      htmlContent += `</div>`;

      if (historial.length === 0) {
        htmlContent += `<p style="text-align: center; color: #9ca3af; padding: 20px;">No hay historial para esta comisión</p>`;
      } else {
        // Tabla desktop
        htmlContent += `<div style="display: ${isMobile ? 'none' : 'block'}; overflow-x: auto;">`;
        htmlContent += `<table style="width: 100%; border-collapse: collapse; font-size: 0.8rem;">`;
        htmlContent += `<tr style="background: linear-gradient(135deg, #f0fdf4, #ecfdf5); border-bottom: 2px solid #bbf7d0;">
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: left;">Fecha</th>
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: left;">Tipo</th>
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: right;">%</th>
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: right;">Fijo</th>
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: center;">Activo</th>
          <th style="padding: 10px 8px; color: #1e5a3f; font-weight: 600; text-align: left;">Responsable</th>
        </tr>`;

        historial.forEach((h: ComisionBancaria, i: number) => {
          const fecha = new Date(h.createdAt).toLocaleString('es-VE');
          const bgRow = i % 2 === 0 ? '#ffffff' : '#fafafa';
          const activoBg = h.activo === 1 ? '#dcfce7' : '#f3f4f6';
          const activoColor = h.activo === 1 ? '#16a34a' : '#6b7280';
          htmlContent += `<tr style="background: ${bgRow}; border-bottom: 1px solid #f3f4f6;">
            <td style="padding: 8px; color: #374151;">${fecha}</td>
            <td style="padding: 8px;"><span style="background: ${h.tipoValor === 'porcentaje' ? '#dcfce7' : '#dbeafe'}; color: ${h.tipoValor === 'porcentaje' ? '#16a34a' : '#2563eb'}; padding: 2px 8px; border-radius: 10px; font-size: 0.75rem; font-weight: 600;">${h.tipoValor}</span></td>
            <td style="padding: 8px; text-align: right; font-weight: 600; color: #1e5a3f;">${h.porcentaje != null ? h.porcentaje + '%' : '-'}</td>
            <td style="padding: 8px; text-align: right; font-weight: 600; color: #1e5a3f;">${h.valorFijo != null ? h.valorFijo : '-'}</td>
            <td style="padding: 8px; text-align: center;"><span style="background: ${activoBg}; color: ${activoColor}; padding: 2px 8px; border-radius: 10px; font-size: 0.75rem; font-weight: 600;">${h.activo === 1 ? 'Sí' : 'No'}</span></td>
            <td style="padding: 8px; color: #6b7280; font-size: 0.75rem;">${h.responsableEmail}</td>
          </tr>`;
        });

        htmlContent += `</table></div>`;

        // Cards mobile
        htmlContent += `<div style="display: ${isMobile ? 'flex' : 'none'}; flex-direction: column; gap: 8px;">`;
        historial.forEach((h: ComisionBancaria) => {
          const fecha = new Date(h.createdAt).toLocaleString('es-VE');
          const activoBg = h.activo === 1 ? '#dcfce7' : '#f3f4f6';
          const activoColor = h.activo === 1 ? '#16a34a' : '#6b7280';
          htmlContent += `<div style="background: #fafafa; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <span style="font-size: 0.75rem; color: #6b7280;">${fecha}</span>
              <span style="background: ${activoBg}; color: ${activoColor}; padding: 2px 8px; border-radius: 10px; font-size: 0.7rem; font-weight: 600;">${h.activo === 1 ? 'Activo' : 'Inactivo'}</span>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">
              <div>
                <span style="font-size: 0.7rem; color: #9ca3af; text-transform: uppercase;">Tipo</span>
                <p style="margin: 0; font-size: 0.8rem; font-weight: 600; color: #374151;">${h.tipoValor}</p>
              </div>
              <div>
                <span style="font-size: 0.7rem; color: #9ca3af; text-transform: uppercase;">${h.tipoValor === 'porcentaje' ? 'Porcentaje' : 'Valor Fijo'}</span>
                <p style="margin: 0; font-size: 0.8rem; font-weight: 700; color: #1e5a3f;">${h.tipoValor === 'porcentaje' ? (h.porcentaje != null ? h.porcentaje + '%' : '-') : (h.valorFijo != null ? h.valorFijo : '-')}</p>
              </div>
            </div>
            <p style="margin: 6px 0 0; font-size: 0.7rem; color: #9ca3af;">${h.responsableEmail}</p>
          </div>`;
        });
        htmlContent += `</div>`;
      }

      htmlContent += `</div>`;

      Swal.fire({
        title: 'Historial de Comisiones',
        html: htmlContent,
        width: isMobile ? '95vw' : '800px',
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#1C593D',
        customClass: { popup: 'swal-aguacate' }
      });
    } catch (error: any) {
      this.spinner.stop(ref);
      Swal.fire('Error', error?.error?.message || 'No se pudo cargar el historial', 'error');
    }
  }

  getBancoNombre(bancoId: number): string {
    const banco = this.bancos.find(b => b.bancoID === bancoId);
    return banco?.nombre || bancoId.toString();
  }

  getTipoOperacionLabel(tipo: string): string {
    return this.tipoOperacionLabels[tipo] || tipo;
  }

  getTipoCuentaLabel(tipo: string): string {
    return this.tipoCuentaLabels[tipo] || tipo;
  }

  // Validador personalizado para máximo 2 decimales
  maxDecimalsValidator(maxDecimals: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString();
      if (value.includes('.')) {
        const decimals = value.split('.')[1];
        if (decimals && decimals.length > maxDecimals) {
          return { invalidDecimals: true };
        }
      }

      return null;
    };
  }
}
